import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Save, Lock, ShieldCheck, Database, ExternalLink, Download, Loader2, AlertTriangle } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Field } from "./Clients";
import { useAuth } from "../contexts/AuthContext";
import { PageBand } from "../components/layout/PageShell";

const TERMS_OPTIONS = [
  { value: "0", label: "Due on receipt" },
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
];

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

// "12 345 678 901" while typing — spaces never count towards the 11 digits.
export function maskAbn(raw) {
  const d = digitsOnly(raw).slice(0, 11);
  return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8), d.slice(8, 11)].filter(Boolean).join(" ");
}

export function maskBsb(raw) {
  const d = digitsOnly(raw).slice(0, 6);
  return d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d;
}

// ATO checksum: subtract 1 from the first digit, weight the 11 digits by
// 10,1,3,5,7,9,11,13,15,17,19 — the sum must be divisible by 89.
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
export function abnChecksumOk(raw) {
  const d = digitsOnly(raw);
  if (d.length !== 11) return false;
  const nums = d.split("").map(Number);
  nums[0] -= 1;
  return nums.reduce((sum, n, i) => sum + n * ABN_WEIGHTS[i], 0) % 89 === 0;
}

// Normalise every editable field so the dirty check compares meaning, not
// formatting: digits-only for ABN/BSB/account number, trimmed text, null==="",
// and numbers compared as numbers. Prevents false "unsaved changes" prompts.
const normSettings = (f) => {
  if (!f) return "";
  return JSON.stringify({
    name: String(f.name ?? "").trim(),
    business_name: String(f.business_name ?? "").trim(),
    email: String(f.email ?? "").trim(),
    phone: String(f.phone ?? "").trim(),
    abn: digitsOnly(f.abn),
    gst_enabled: f.gst_enabled !== false,
    default_rate: Number(f.default_rate ?? 0),
    payment_terms_days: Number(f.payment_terms_days ?? 7),
    account_name: String(f.account_name ?? "").trim(),
    bsb: digitsOnly(f.bsb),
    account_number: digitsOnly(f.account_number),
  });
};

export default function Settings() {
  const { setUser, user, readOnly } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(null);
  const [savedForm, setSavedForm] = useState(null); // last persisted settings (baseline for the dirty check)
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [abnPrompt, setAbnPrompt] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  const abnRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setForm(data);
      setSavedForm(data);
    })();
  }, []);

  const dirty = useMemo(() => !!form && !!savedForm && normSettings(form) !== normSettings(savedForm), [form, savedForm]);

  const performSave = useCallback(async () => {
    setAbnPrompt(false);
    const abnDigits = digitsOnly(form.abn);
    if (String(form.abn || "").trim() && abnDigits.length !== 11) {
      toast.error("An ABN has 11 digits.");
      abnRef.current?.focus();
      return false;
    }
    const bsbDigits = digitsOnly(form.bsb);
    if (String(form.bsb || "").trim() && bsbDigits.length !== 6) {
      toast.error("A BSB has 6 digits.");
      return false;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        name: form.name,
        business_name: form.business_name,
        abn: abnDigits ? maskAbn(abnDigits) : "",
        phone: form.phone,
        default_rate: Number(form.default_rate),
        gst_enabled: form.gst_enabled !== false,
        account_name: form.account_name,
        bsb: bsbDigits ? maskBsb(bsbDigits) : "",
        account_number: digitsOnly(form.account_number),
        payment_terms_days: Number(form.payment_terms_days ?? 7),
      });
      setForm(data);
      setSavedForm(data);
      if (user) setUser({ ...user, name: data.name });
      toast.success("Settings saved");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, setUser, user]);

  const save = () => {
    // GST on with a blank ABN → warn before saving, every time.
    if (form.gst_enabled !== false && !String(form.abn || "").trim()) {
      setAbnPrompt(true);
      return;
    }
    performSave();
  };

  // Unsaved-changes guard: block in-app navigation and reloads while the GST
  // switch, ABN, bank details or payment terms differ from what was saved.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const onClick = (e) => {
      const anchor = e.target?.closest?.("a[href]");
      if (!anchor || anchor.target === "_blank") return;
      const href = anchor.getAttribute("href");
      if (!href || !href.startsWith("/") || href === "/settings") return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNav(href);
    };
    document.addEventListener("click", onClick, true);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [dirty]);

  // When the trial chip links here with #plan, scroll the Plan card into view.
  useEffect(() => {
    if (form && location.hash === "#plan") {
      document.getElementById("plan")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [form, location.hash]);

  const downloadBackup = async () => {
    setBackingUp(true);
    const t = toast.loading("Preparing your backup. Packing CSVs and invoice PDFs...");
    try {
      const res = await api.get("/backup/download", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `trainmate_backup_${date}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const c = res.headers["x-backup-clients"];
      const s = res.headers["x-backup-sessions"];
      const i = res.headers["x-backup-invoices"];
      toast.success(`Backup ready. ${c} clients, ${s} sessions, ${i} invoices.`, { id: t });
    } catch {
      toast.error("Could not generate backup. Please try again.", { id: t });
    } finally {
      setBackingUp(false);
    }
  };

  if (!form) return <div className="text-[#6B635C]" data-testid="settings-loading">Loading…</div>;

  const gstOn = form.gst_enabled !== false;
  const abnDigits = digitsOnly(form.abn);
  const abnBlank = !String(form.abn || "").trim();
  const abnChecksumWarn = abnDigits.length === 11 && !abnChecksumOk(abnDigits);

  const onAbnChange = (raw) => {
    if (digitsOnly(raw).length > 11) {
      toast.error("An ABN has 11 digits. The extra numbers were trimmed.");
    }
    setForm({ ...form, abn: maskAbn(raw) });
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <PageBand
        testid="settings-band"
        title={
          <div>
            <p style={{ fontSize: 12, fontWeight: 600 }} className="uppercase tracking-widest text-[#D3E8E6]">
              Trainer Profile
            </p>
            <h1 className="font-bold leading-tight mt-1" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
              Settings
            </h1>
          </div>
        }
      />

      <div className="max-w-3xl space-y-8">
      <p className="text-[#6B635C] text-sm">Your details and business info. Used on every invoice PDF.</p>

      <section id="plan" className="tm-card p-6 space-y-4 scroll-mt-20" data-testid="plan-card">
        <h2 className="font-display text-2xl font-bold uppercase mb-1">Plan</h2>
        {user?.trial?.exempt ? (
          <p className="text-sm text-[#2A2623]" data-testid="plan-status">Your account has full access.</p>
        ) : user?.trial?.active ? (
          <p className="text-sm font-semibold text-[#0F6B6B]" data-testid="plan-status">
            Free trial · {user.trial.days_left} day{user.trial.days_left === 1 ? "" : "s"} left
          </p>
        ) : (
          <p className="text-sm font-semibold text-[#7A5E18]" data-testid="plan-status">Your free trial has ended.</p>
        )}
        <p className="text-sm text-[#2A2623]" data-testid="plan-price">
          <span className="font-display text-2xl font-black text-[#2A2623]">$29 AUD</span> per month, GST included
        </p>
        <Button
          disabled
          data-testid="plan-subscribe-button"
          className="bg-[#0F6B6B] text-[#FFFFFF] font-bold uppercase tracking-wide"
        >
          Coming soon
        </Button>
      </section>

      <section className="tm-card p-6 space-y-5" data-testid="profile-card">
        <h2 className="font-display text-2xl font-bold uppercase mb-1">Profile</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Display name">
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" className="bg-[#FAF7F2] border-[#E8E1D6]" />
          </Field>
          <Field label="Email">
            <Input value={form.email || ""} disabled data-testid="settings-email" className="bg-[#FAF7F2] border-[#E8E1D6] text-[#6B635C]" />
          </Field>
        </div>
      </section>

      <section className="tm-card p-6 space-y-5" data-testid="business-card">
        <h2 className="font-display text-2xl font-bold uppercase mb-1">Business</h2>
        <Field label="Business name">
          <Input value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Bennett Strength Co." data-testid="settings-business" className="bg-[#FAF7F2] border-[#E8E1D6]" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="ABN">
            <Input
              ref={abnRef}
              value={form.abn || ""}
              onChange={(e) => onAbnChange(e.target.value)}
              inputMode="numeric"
              placeholder="12 345 678 901"
              data-testid="settings-abn"
              className="bg-[#FAF7F2] border-[#E8E1D6]"
            />
          </Field>
          <Field label="Phone">
            <Input
              value={form.phone || ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+()\s-]/g, "") })}
              inputMode="tel"
              placeholder="+61 4__ ___ ___"
              data-testid="settings-phone"
              className="bg-[#FAF7F2] border-[#E8E1D6]"
            />
          </Field>
        </div>
        {!abnBlank && abnDigits.length !== 11 && (
          <p className="text-xs text-[#A8372A]" data-testid="settings-abn-length-error">An ABN has 11 digits.</p>
        )}
        {abnChecksumWarn && (
          <p className="text-xs text-[#7A5E18]" data-testid="settings-abn-checksum-warning">
            This doesn&apos;t look like a valid ABN. Check it.
          </p>
        )}
      </section>

      <section className="tm-card p-6 space-y-5" data-testid="billing-card">
        <h2 className="font-display text-2xl font-bold uppercase mb-1">Billing</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Price per class (AUD, incl. GST)">
            <Input type="number" min="0" step="1" value={form.default_rate} onChange={(e) => setForm({ ...form, default_rate: e.target.value })} data-testid="settings-rate" className="bg-[#FAF7F2] border-[#E8E1D6]" />
          </Field>
          <Field label="Currency">
            <LockedInput value="AUD" testid="settings-currency" />
          </Field>
          <Field label="Timezone">
            <LockedInput value="Australia / Sydney (AEST)" testid="settings-timezone" />
          </Field>
        </div>

        <Field label="Payment terms">
          <Select
            value={String(form.payment_terms_days ?? 7)}
            onValueChange={(v) => setForm({ ...form, payment_terms_days: Number(v) })}
          >
            <SelectTrigger data-testid="settings-payment-terms" className="bg-[#FAF7F2] border-[#E8E1D6]">
              <SelectValue placeholder="7 days" />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]">
              {TERMS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`settings-terms-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <p className="text-xs text-[#6B635C]">
          Every invoice gets its due date when you send it — issue date plus your terms. Changing this never moves the
          due date on an invoice you have already sent.
        </p>
        <p className="text-xs text-[#6B635C]">Price per class is what the client pays. {gstOn ? "GST (10%) is calculated out of that total." : "No GST is taken out of that total."} Currency &amp; timezone are locked for the AU edition.</p>

        <div
          className="flex items-start justify-between gap-4 rounded-md bg-[#FAF7F2] border border-[#E8E1D6] px-4 py-3.5"
          data-testid="gst-setting-row"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold">Charge GST (10%)</p>
            <p className="text-xs text-[#6B635C] mt-1 leading-relaxed">
              Turn this off only if you are not registered for GST. Registration is required once you earn $75,000 a year. Check with your accountant if unsure.
            </p>
          </div>
          <Switch
            checked={gstOn}
            onCheckedChange={(v) => setForm({ ...form, gst_enabled: v })}
            data-testid="settings-gst-toggle"
            aria-label="Charge GST"
            className="mt-0.5 shrink-0 data-[state=checked]:bg-[#0F6B6B]"
          />
        </div>
        {gstOn && abnBlank && (
          <p
            className="flex items-start gap-2 text-xs text-[#7A5E18] bg-[#FBF4DD] border border-[#ECDCA6] rounded px-3 py-2"
            data-testid="settings-abn-warning"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            GST is on but your ABN is blank. New invoices will wait as drafts until you add it — a tax invoice needs an ABN.
          </p>
        )}
        {!gstOn && (
          <p className="text-xs text-[#6B635C]" data-testid="settings-gst-off-note">
            New invoices will be titled &ldquo;Invoice&rdquo; and show &ldquo;GST not applicable&rdquo;. Invoices you have already created keep their original treatment.
          </p>
        )}
      </section>

      <section className="tm-card p-6 space-y-5" data-testid="bank-card">
        <div>
          <h2 className="font-display text-2xl font-bold uppercase mb-1">Bank transfer</h2>
          <p className="text-xs text-[#6B635C]">Optional. When filled in, these details appear on every invoice PDF under &quot;How to pay&quot; so clients can pay you directly. Leave blank to hide.</p>
        </div>
        <Field label="Account name">
          <Input value={form.account_name || ""} onChange={(e) => setForm({ ...form, account_name: e.target.value })} placeholder="Bennett Strength Co." data-testid="settings-account-name" className="bg-[#FAF7F2] border-[#E8E1D6]" />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="BSB">
            <Input
              value={form.bsb || ""}
              onChange={(e) => setForm({ ...form, bsb: maskBsb(e.target.value) })}
              inputMode="numeric"
              placeholder="123-456"
              data-testid="settings-bsb"
              className="bg-[#FAF7F2] border-[#E8E1D6]"
            />
          </Field>
          <Field label="Account number">
            <Input
              value={form.account_number || ""}
              onChange={(e) => setForm({ ...form, account_number: digitsOnly(e.target.value).slice(0, 9) })}
              inputMode="numeric"
              placeholder="12345678"
              data-testid="settings-account-number"
              className="bg-[#FAF7F2] border-[#E8E1D6]"
            />
          </Field>
        </div>
      </section>

      <div className="flex items-center justify-end gap-3">
        {dirty && (
          <span className="text-xs text-[#7A5E18]" data-testid="settings-dirty-note">Unsaved changes</span>
        )}
        <Button onClick={save} disabled={saving || readOnly} title={readOnly ? "Your account is read only" : undefined} data-testid="settings-save" className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold disabled:opacity-40">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>

      <section className="tm-card p-6 space-y-5" data-testid="data-protection-card">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#0F6B6B]/15 border border-[#0F6B6B]/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#0F6B6B]" />
          </div>
          <h2 className="font-display text-2xl font-bold uppercase">Data protection</h2>
        </div>

        <div className="flex items-start gap-3 rounded-md bg-[#FAF7F2] border border-[#E8E1D6] px-4 py-3.5" data-testid="backup-trust-badge">
          <div className="relative flex h-2.5 w-2.5 mt-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0F6B6B] opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#0F6B6B]" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#2A2623]">
              <span className="text-[#0F6B6B]">Last backup: within 1 hour</span>
              <span className="text-[#6B635C] font-normal"> · encrypted, off-site</span>
            </p>
            <p className="text-xs text-[#6B635C] mt-1 leading-relaxed">
              Automatic snapshots run every hour, retained for 7 days. Daily / weekly / monthly / yearly
              layers extend the safety net up to 1 year with 7-day point-in-time recovery.
            </p>
          </div>
          <Database className="w-4 h-4 text-[#6B635C] flex-shrink-0 mt-1" />
        </div>

        <div className="rounded-md bg-[#FAF7F2] border border-[#E8E1D6] p-5" data-testid="account-backup-panel">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#0F6B6B] font-semibold mb-1.5">Accountant-ready backup</p>
              <p className="font-display text-lg font-bold uppercase leading-tight">Backup my account</p>
              <p className="text-xs text-[#6B635C] mt-1.5 leading-relaxed">
                One ZIP with <span className="text-[#2A2623]">clients.csv</span>, <span className="text-[#2A2623]">sessions.csv</span>, <span className="text-[#2A2623]">invoices.csv</span> and individual invoice PDFs for the last 5 financial years (ATO retention). Hand it straight to your accountant.
              </p>
            </div>
            <Button
              onClick={downloadBackup}
              disabled={backingUp}
              data-testid="download-backup-btn"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold flex-shrink-0"
            >
              {backingUp ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Preparing...</>
              ) : (
                <><Download className="w-4 h-4" /> Download ZIP</>
              )}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs pt-1">
          <Link to="/privacy" target="_blank" rel="noreferrer" className="text-[#6B635C] hover:text-[#0F6B6B] transition-colors inline-flex items-center gap-1" data-testid="settings-privacy-link">
            Privacy Policy <ExternalLink className="w-3 h-3" />
          </Link>
          <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#6B635C] hover:text-[#0F6B6B] transition-colors inline-flex items-center gap-1" data-testid="settings-terms-link">
            Terms <ExternalLink className="w-3 h-3" />
          </Link>
          <Link to="/data-deletion" target="_blank" rel="noreferrer" className="text-[#6B635C] hover:text-[#0F6B6B] transition-colors inline-flex items-center gap-1" data-testid="settings-data-deletion-link">
            Data deletion <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </section>
      </div>

      <AlertDialog open={abnPrompt} onOpenChange={(o) => !o && setAbnPrompt(false)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="abn-save-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">GST is on</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              GST is on but you haven&apos;t added your ABN. Invoices will wait as drafts until you add it. Add ABN now,
              or save anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => { setAbnPrompt(false); setTimeout(() => abnRef.current?.focus(), 50); }}
              data-testid="abn-warning-add"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Add ABN
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); performSave(); }}
              data-testid="abn-warning-save-anyway"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              Save anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingNav} onOpenChange={(o) => !o && setPendingNav(null)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              You have unsaved changes. Save or discard?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                const to = pendingNav;
                setPendingNav(null);
                setForm(savedForm); // discard edits — restore the last saved values
                setTimeout(() => navigate(to), 0);
              }}
              data-testid="unsaved-discard"
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Discard
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                const to = pendingNav;
                const ok = await performSave();
                setPendingNav(null);
                if (ok) setTimeout(() => navigate(to), 0);
              }}
              data-testid="unsaved-save"
              className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold"
            >
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LockedInput({ value, testid }) {
  return (
    <div className="relative">
      <Input value={value} disabled data-testid={testid} className="bg-[#FAF7F2] border-[#E8E1D6] text-[#6B635C] pr-9" />
      <Lock className="w-3.5 h-3.5 text-[#6B635C] absolute right-3 top-1/2 -translate-y-1/2" />
    </div>
  );
}
